import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Divider, useTheme } from '@mui/material';
import { glass, ADMIN_GRADIENTS } from './glassTokens';

/**
 * GlassDialog — Dialog with glassmorphic paper surface and blurred backdrop.
 * Accepts all standard MUI Dialog props.
 *
 * Usage:
 *   <GlassDialog open={...} onClose={...} title="..." maxWidth="md" fullWidth>
 *     <GlassDialogSection label="Section Title" />
 *     ... content ...
 *     <DialogActions>...</DialogActions>
 *   </GlassDialog>
 */
export function GlassDialog({ title, titleAction, children, sx = {}, ...rest }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const g = glass(theme);

  return (
    <Dialog
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          background: isDark ? 'rgba(2,6,23,0.6)' : 'rgba(15,23,42,0.35)',
        },
      }}
      PaperProps={{
        sx: {
          ...g.surfaceStrong,
          borderRadius: 3,
          backgroundImage: 'none',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: ADMIN_GRADIENTS.accent,
          },
          ...sx,
        },
      }}
      {...rest}
    >
      {title && (
        <DialogTitle sx={{ fontWeight: 700, letterSpacing: '-0.01em', pb: 1 }}>
          {titleAction ? (
            <Box display="flex" justifyContent="space-between" alignItems="center">
              {title}
              {titleAction}
            </Box>
          ) : title}
        </DialogTitle>
      )}
      {children}
    </Dialog>
  );
}

/**
 * GlassDialogSection — visual section divider inside a GlassDialog.
 */
export function GlassDialogSection({ label }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box sx={{ px: 3, pt: 2, pb: 1 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontSize: '0.68rem',
          color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)',
        }}
      >
        {label}
      </Typography>
      <Divider
        sx={{
          mt: 0.75,
          borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)',
        }}
      />
    </Box>
  );
}

export default GlassDialog;
